import type { StrokeData } from "./bishun-drawing.js";
import { after, flyText, wantsStillness } from "./fuxi-assembly-flight.js";
import { boundOnce } from "./bound-once.js";

/**
 * Putting the character together on a marked card.
 *
 * The question card leaves the reader's parts as a sum, `女 + 子`, and the
 * marked card draws the square under the verdict holding the character. This
 * takes the character apart again and plays the reader's answer into it.
 *
 * 1. Once the square has opened, each part flies from the reader's sum to the
 *    box it would take in the character, a moment after the one before.
 * 2. A part arriving in its own box lands as the character's own strokes for
 *    that part, out of the outline the 笔顺 drawings fetch. Any other part,
 *    a decoy or a right part in another part's box, tries its box and falls
 *    away. It has no strokes in the character to land as.
 * 3. The rest of the character fades in, and its parts light up one after
 *    another, beside the same parts in the sum under the square.
 *
 * **A reader who asked for less motion sees the character, still.** So does a
 * reader whose outline file does not arrive. The markup draws the character in
 * the web font, hidden until this has either played the answer or marked the
 * square `cb-fuxi-zuzi-still`, so the answer is never shown before it plays.
 *
 * The boxes, the part each wants and the strokes it owns are on the square's
 * SVG, which `src/user/fuxi-assembly-markup.ts` draws.
 */

/** The grid the outlines are drawn on, and the transform that turns it up. */
const toSquare = "scale(0.09765625 -0.09765625) translate(0 -900)";

/** The SVG namespace, which every element drawn into the square is made in. */
const svg = "http://www.w3.org/2000/svg";

/** The event htmx dispatches over content it has just taken into the page. */
const processed = "htmx:after:process";

/**
 * How long the square takes to open before the first part sets off, which is
 * the opening and the 米字格 fading in that `modules/fuxi` draws.
 */
const opening = 600;

/** How long a part takes to fly from the sum into the square. */
const flight = 520;

/** How long after one part the next one sets off. */
const stagger = 260;

/** How long each part stays lit as the parts light up in turn. */
const lit = 520;

/** The squares already played, so htmx's second pass plays none twice. */
const once = boundOnce();

/** One character's outlines, or nothing where they cannot be had. */
async function outlines(address: string): Promise<StrokeData | undefined> {
  try {
    const response = await fetch(address);

    return response.ok ? ((await response.json()) as StrokeData) : undefined;
  } catch {
    return undefined;
  }
}

/** A group of the character's strokes, in the square's units. */
function strokeGroup(
  page: Document,
  data: StrokeData,
  strokes: readonly number[],
): SVGGElement {
  const group = page.createElementNS(svg, "g");

  group.setAttribute("transform", toSquare);

  for (const stroke of strokes) {
    const path = page.createElementNS(svg, "path");

    path.setAttribute("d", data.strokes[stroke] ?? "");
    group.append(path);
  }

  return group;
}

/** The strokes a box's part owns, off the box. */
function strokesOf(box: Element): readonly number[] {
  return (box.getAttribute("data-cb-fuxi-box-strokes") ?? "")
    .split(" ")
    .filter(Boolean)
    .map(Number);
}

/** Where a box stands on the page, from the square's own place. */
function boxOnPage(frame: Element, box: Element): DOMRect {
  const square = frame.getBoundingClientRect();
  const at = (name: string): number =>
    Number(box.getAttribute(name) ?? 0) / 100;

  return new DOMRect(
    square.left + square.width * at("x"),
    square.top + square.height * at("y"),
    square.width * at("width"),
    square.height * at("height"),
  );
}

/** Light each part in turn, in the square and in the sum. */
async function lightInTurn(
  card: Element,
  parts: readonly SVGGElement[],
): Promise<void> {
  const page = card.ownerDocument;
  const turn = lit + 80;

  await Promise.all(
    parts.map(async (part, at) => {
      const lighting = [
        part,
        ...card.querySelectorAll(`[data-cb-fuxi-part-of="${String(at)}"]`),
      ];

      await after(page, at * turn);
      lighting.forEach((one) => one.classList.add("cb-fuxi-zuzi-lit"));
      await after(page, lit);
      lighting.forEach((one) => one.classList.remove("cb-fuxi-zuzi-lit"));
    }),
  );
}

/** Play the reader's answer into one square. */
async function play(square: Element): Promise<void> {
  const page = square.ownerDocument;
  const card = square.closest("section") ?? page.body;
  const frame = square.querySelector("[data-cb-fuxi-zuzi-frame]");

  if (frame === null || wantsStillness(page)) {
    square.classList.add("cb-fuxi-zuzi-still");

    return;
  }

  const data = await outlines(
    frame.getAttribute("data-cb-fuxi-zuzi-outlines") ?? "",
  );

  if (data === undefined || !square.isConnected) {
    square.classList.add("cb-fuxi-zuzi-still");

    return;
  }

  const boxes = [...frame.querySelectorAll("[data-cb-fuxi-box]")];
  const parts = boxes.map((box) => {
    const group = strokeGroup(page, data, strokesOf(box));

    group.setAttribute("class", "cb-fuxi-zuzi-part");
    frame.append(group);

    return group;
  });

  const terms = [
    ...card.querySelectorAll("[data-cb-fuxi-zuzi-yours] [data-cb-fuxi-slot]"),
  ];
  const flights = terms.map(async (term, at) => {
    const box = boxes[at];

    if (box === undefined) {
      return;
    }

    await after(page, opening + at * stagger);

    const right = term.getAttribute("data-cb-fuxi-mark") === "correct";

    await flyText({
      from: term,
      to: boxOnPage(frame, box),
      duration: flight,
      fall: !right,
    });

    if (right) {
      parts[at]?.classList.add("cb-fuxi-zuzi-landed");
    }
  });

  await Promise.all(flights);
  await after(page, 200);
  square.classList.add("cb-fuxi-zuzi-whole-in");
  await after(page, 600);
  await lightInTurn(card, parts);
}

/**
 * Play every marked square inside one piece of the page that has not been
 * played. A 拼字 Exercise calls it over each character it marks.
 * @param within - The piece of the page.
 */
export function playWithin(within: ParentNode): void {
  for (const square of once(within, "[data-cb-fuxi-zuzi-done]")) {
    void play(square);
  }
}

/**
 * Put the character together on every marked card that arrives.
 *
 * @param page - The page the bundle is running on.
 */
export function startFuxiAssembled(page: Document): void {
  playWithin(page);
  page.addEventListener(processed, (event: Event) => {
    if (event.target instanceof Element) {
      playWithin(event.target);
    }
  });
}
